/**
 * Client for the PUBLIC keyword audit (`/audit`), the lead magnet.
 *
 * Separate from `lib/admin.ts` for one reason that matters: that module's helpers
 * attach a session token and bounce to `/admin/login` on a 401 (`handle401`). This
 * surface has no session and no login to bounce to, so borrowing them would turn a
 * transient error on a public page into a redirect nobody asked for.
 *
 * `tailAudit` is the one signed-in call: the Discovery page reads a stored audit over the
 * `/v1` transport, and shares the frame reader because the frames are identical.
 *
 * DEADLINES ARE PER CALL, not module-wide. The audit itself streams and carries no
 * abort signal — a stream that delivers over time cannot be judged by a stopwatch —
 * while the short request/response calls below each pass their own.
 */
import { ApiError } from '@api/errors';
import { readFrames } from '@api/sse';
import { openAuditStream } from '@api';

const API_BASE = import.meta.env['VITE_API_BASE'] ?? 'http://localhost:8000';

export interface Leader {
  name: string;
  /** Null when the group record could not be read — render the name unlinked. */
  slug: string | null;
  /** 1-based position for this keyword. 0 on the suggest surface, which has no ranking. */
  rank: number;
  /** True for the audited community itself — mark it rather than hide it. */
  is_you: boolean;
  logo_url: string;
  total_members: number;
  /** Minor units (cents). Null for a free community. */
  price_amount: number | null;
  price_currency: string;
  /** "month", "year", or "" for one-off / free. */
  price_interval: string;
}

export interface Standing {
  keyword: string;
  n_hits: number;
  /** Null means unranked: outside the index's horizon, or absent entirely. */
  rank: number | null;
  /** False when the keyword was proposed rather than supplied by the visitor. */
  asked: boolean;
  /** How many competitors also target it. Filled by the `support` frame, which
   *  arrives after every row — a row is emitted before we know who the competitors
   *  are, so the count cannot ride on it. */
  used_by?: number;
  leaders: Leader[];
}

export interface Recommendation {
  keyword: string;
  n_hits: number;
  rank: number | null;
  /** How many of the leaders found in stage one target this keyword. */
  used_by: number;
}

export interface Community {
  slug: string;
  display_name: string;
  logo_url: string;
  total_members: number;
  /**
   * The discovery language this run measured in — `all` for an unfiltered run, null for
   * a run recorded before we stored it. It arrives with the run rather than being held
   * by whoever started it, so a REPLAYED audit links to the same field it measured.
   */
  language: string | null;
}


/** The readable reason on a failed response: the public routes' `detail`, or the `/v1`
 *  envelope's `error.message`. */
async function detail(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { detail?: string; error?: { message?: string } };
    return body.detail ?? body.error?.message ?? `${res.status} ${res.statusText}`;
  } catch {
    return `${res.status} ${res.statusText}`;
  }
}

async function post(path: string, body: unknown, timeoutMs: number): Promise<Response> {
  try {
    return await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: AbortSignal.timeout(timeoutMs),
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'TimeoutError') {
      throw new ApiError(0, 'That took too long — try again in a moment.');
    }
    throw err;
  }
}




/** What the stream reports, in the order the server sends it. */
export interface AuditHandlers {
  onCommunity: (c: Community) => void;
  /** Every keyword that will be checked — render the skeleton from this. */
  onPlan: (keywords: string[]) => void;
  /** One finished row. Arrives in COMPLETION order, not plan order. */
  onRow: (row: Standing) => void;
  /** Competitor counts for the audited keywords, keyed by keyword. */
  onSupport: (support: Record<string, number>) => void;
  onRecommendations: (r: {
    high_volume: Recommendation[];
    winnable: Recommendation[];
    candidates_dropped: number;
  }) => void;
  onDone: (auditId: number) => void;
}

/**
 * Run an audit and report it as it happens.
 *
 * NO TIMEOUT SIGNAL. A deadline on the whole request is wrong for a stream: the point is
 * that it delivers over time, and the visitor can see it doing so. The failure a timeout
 * guards against — a server that says nothing — is visible here as a table that stops
 * filling, which is information a spinner could never give.
 */
export async function streamAudit(
  url: string,
  keywords: string[],
  handlers: AuditHandlers,
  language?: string | null,
): Promise<void> {
  const res = await fetch(`${API_BASE}/audit/stream`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(language ? { url, keywords, language } : { url, keywords }),
  });
  return readAudit(res, handlers);
}

/**
 * Read a stored audit — replayed whole if it finished, live if it is still running. The
 * admin Discovery page's source, over the signed-in `/v1` transport rather than this
 * module's anonymous fetch; the frames are the same, so the same reader takes them.
 */
export async function tailAudit(auditId: number, handlers: AuditHandlers): Promise<void> {
  return readAudit(await openAuditStream(auditId), handlers);
}

/**
 * Hand one audit stream's frames to `handlers`, in the order the server sends them.
 *
 * Errors split the way the endpoints do: anything before the first byte is a normal
 * status and throws `ApiError`; anything after arrives as an `error` frame and throws with
 * the detail the server sent.
 */
async function readAudit(res: Response, handlers: AuditHandlers): Promise<void> {
  if (!res.ok) throw new ApiError(res.status, await detail(res));
  if (!res.body) throw new ApiError(0, 'The server sent an empty response.');

  let failure: string | null = null;
  await readFrames(res.body, (event, data) => {
    const parsed: unknown = JSON.parse(data);
    switch (event) {
      case 'community':
        handlers.onCommunity(parsed as Community);
        return false;
      case 'plan':
        handlers.onPlan((parsed as { keywords: string[] }).keywords);
        return false;
      case 'row':
        handlers.onRow(parsed as Standing);
        return false;
      case 'support':
        handlers.onSupport(parsed as Record<string, number>);
        return false;
      case 'recommendations':
        handlers.onRecommendations(
          parsed as {
            high_volume: Recommendation[];
            winnable: Recommendation[];
            candidates_dropped: number;
          },
        );
        return false;
      case 'done':
        handlers.onDone((parsed as { audit_id: number }).audit_id);
        return true;
      case 'error':
        failure = (parsed as { detail: string }).detail;
        return true;
      default:
        return false;
    }
  });
  if (failure !== null) throw new ApiError(0, failure);
}

/** `8700, "usd", "month"` -> `"$87/mo"`. Null amount is a free community. */
export function formatPrice(
  amount: number | null,
  currency: string,
  interval: string,
): string {
  if (!amount) return 'Free';
  const symbol = currency.toLowerCase() === 'usd' ? '$' : '';
  // Skool stores minor units. Whole amounts are the norm, so only show cents when
  // there are any — "$87/mo" rather than "$87.00/mo".
  const major = amount / 100;
  const shown = Number.isInteger(major) ? String(major) : major.toFixed(2);
  const period = interval === 'month' ? '/mo' : interval === 'year' ? '/yr' : '';
  return `${symbol}${shown}${currency && !symbol ? ` ${currency.toUpperCase()}` : ''}${period}`;
}


/**
 * How a keyword's field size reads at a glance. Shared by the audit table and the
 * improve page, which show the same measurement and must not disagree about where the
 * bands fall.
 *
 *   red     under 1000   below the target — too small a field to be worth targeting
 *   green   1000-4999    at target, and still a field a small community can place in
 *   purple  5000+        far over — plenty of demand, and plenty of competition
 *
 * 1000 is the floor the recommendations already use (`MIN_RESULTS_FLOOR` in
 * `domain/keyword_audit.py`); OVER_TARGET is the UI's own reading of "well over" and is
 * the one number here that was a judgement call.
 */
export const TARGET = 1000;
export const OVER_TARGET = 5000;
/**
 * The bar's full width. Fields run from single digits to ~40,000, so it is scaled
 * LOGARITHMICALLY — a linear bar would render everything below 5,000 as a stub and
 * throw away the distinction the bands exist to draw.
 */
const BAR_MAX = 20000;

export function volumeColor(n: number): string {
  if (n < TARGET) return 'bar-under';
  return n < OVER_TARGET ? 'bar-target' : 'bar-over';
}

export function volumeWidth(n: number): number {
  if (n <= 0) return 0;
  const pct = (Math.log10(n + 1) / Math.log10(BAR_MAX + 1)) * 100;
  return Math.max(4, Math.min(100, pct));
}

export function volumeLabel(n: number): string {
  if (n < TARGET) return `${n.toLocaleString()} — under the ${TARGET.toLocaleString()} target`;
  return n < OVER_TARGET
    ? `${n.toLocaleString()} — at target`
    : `${n.toLocaleString()} — well over target, heavily contested`;
}


/** A keyword the picker can choose from, with what the audit measured about it. */
export interface SelectCandidate {
  keyword: string;
  n_hits: number;
  used_by: number;
  /** Whether the community already targets it. */
  current: boolean;
}

/**
 * Ask the model which eleven keywords to run with — 7 from the high band, 4 from the
 * low one. The quota is enforced server-side, so the reply is always eleven (or every
 * candidate in a band that has fewer).
 */
export async function chooseKeywords(
  url: string,
  candidates: SelectCandidate[],
  language?: string | null,
): Promise<string[]> {
  const res = await post('/audit/select',
    language ? { url, candidates, language } : { url, candidates }, 60_000);
  if (!res.ok) throw new ApiError(res.status, await detail(res));
  return ((await res.json()) as { keywords: string[] }).keywords;
}


/**
 * The top few communities for one keyword — fired from a stage-three hover.
 *
 * Cheap on the server (the search is already cached from the audit), so this is safe
 * to call per row the reader points at. Callers should still cache per keyword: a
 * mouse crossing a table would otherwise re-ask for the same row repeatedly.
 */
export async function fetchLeaders(
  url: string,
  keyword: string,
  language?: string | null,
): Promise<Leader[]> {
  const params = new URLSearchParams({ url, keyword });
  if (language) params.set('language', language);
  const res = await fetch(`${API_BASE}/audit/leaders?${params}`, {
    signal: AbortSignal.timeout(20_000),
  });
  if (!res.ok) throw new ApiError(res.status, await detail(res));
  return (await res.json()) as Leader[];
}

/** Skool's own discovery search for a keyword — the page these numbers describe. */
export interface CommunityPreview {
  slug: string;
  display_name: string;
  logo_url: string;
  total_members: number;
  /** Null when the community is not in Skool's index — it ranks nowhere, which the
   *  audit itself will report. The picker must not present null as a language. */
  language: string | null;
}

/**
 * What a pasted link points at, before anything is measured — so the language picker can
 * say "German" the moment somebody pastes a German community.
 *
 * Returns null for anything that is not a resolvable Skool community, including a
 * half-typed URL: this runs while somebody is still typing, so failing is ordinary and
 * must never surface as an error or block the form.
 */
export async function previewCommunity(
  url: string,
  signal?: AbortSignal,
): Promise<CommunityPreview | null> {
  try {
    const params = new URLSearchParams({ url });
    // Built conditionally rather than passing `{ signal: undefined }`: the project runs
    // `exactOptionalPropertyTypes`, so an explicit undefined is not the same as omitting.
    const init: RequestInit = signal ? { signal } : {};
    const res = await fetch(`${API_BASE}/audit/preview?${params}`, init);
    if (!res.ok) return null;
    return (await res.json()) as CommunityPreview;
  } catch {
    return null;
  }
}

export interface LanguageOption {
  /** What `?lang=` and the index's own `language` field use. `all` drops the filter. */
  value: string;
  label: string;
}

/**
 * Skool's discovery languages, served by the API rather than shipped in this file.
 *
 * One list, one place: the public tool and the in-product one render the same options,
 * and neither can drift from the index when Skool adds a language. Falls back to the
 * two that matter if the call fails — an empty dropdown would be worse than a short one.
 */
export async function fetchLanguages(): Promise<LanguageOption[]> {
  try {
    const res = await fetch(`${API_BASE}/audit/languages`, {
      signal: AbortSignal.timeout(10_000),
    });
    if (!res.ok) throw new ApiError(res.status, await detail(res));
    return (await res.json()) as LanguageOption[];
  } catch {
    return [
      { value: 'all', label: 'All languages' },
      { value: 'english', label: 'English' },
    ];
  }
}

export function discoveryUrl(keyword: string, language?: string | null): string {
  // The `lang` is not cosmetic: discovery returns a different field per language, so a
  // link without it sends someone who was ranked in German to the English results and
  // invites them to conclude the number above it is wrong. `all` is Skool's own spelling
  // for the unfiltered view.
  // No default to `all`: a link that drops the filter shows a different field from the
  // one the row was measured in. Callers pass the run's own language; only a caller that
  // genuinely has none falls through to English, which is what discovery itself shows.
  const lang = language ?? 'english';
  return `https://www.skool.com/discovery?q=${encodeURIComponent(keyword)}`
    + `&lang=${encodeURIComponent(lang)}`;
}
